import { Action, createAction, handleActions } from "redux-actions";
import moment from "moment";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import {
  FechaFormulario,
  GetAseguradoResponse,
  GetRamasResponse,
  GetUsuariosResponse,
  PostPolizaMultiRamaRequest,
  PostPolizaMultiRamaResponse,
  Usuario,
} from "~api/types";
import {
  CampoFormulario,
  Dispatch,
  GetState,
  RamaOption,
  RamaSeleccionada,
  Services,
  ServicesUsuarios,
  UsuarioOption,
  UsuarioSeleccionado,
} from "../types";

/** Forma del error que devuelve el backend cuando rechaza el alta. */
interface AltaPolizaError {
  responseStatus: { errors: { message: string }[] };
}

interface AltaPolizaMultiramaState {
  submitting: boolean;
  numeroPolizaSelected: CampoFormulario<string | null>;
  aseguradoNombreSelected: CampoFormulario<string | null>;
  aseguradoApellidoSelected: CampoFormulario<string | null>;
  aseguradoIdSelected: string | null;
  aseguradoNumeroDocumentoSelected: CampoFormulario<string | null>;
  dominioSelected: CampoFormulario<string | null>;
  ramaSelected: RamaSeleccionada;
  asegurado: GetAseguradoResponse | null;
  ramasFilter: RamaOption[] | null;
  ramasOptions: Record<string, string>;
  companiaSelected: CampoFormulario<string>;
  coberturaSelected: CampoFormulario<string | null>;
  usuarioSelected: UsuarioSeleccionado;
  usuariosFilter: UsuarioOption[];
  usuariosOptions: Record<string, string>;
  usuarios?: Usuario[];
  vigenciaSelected: CampoFormulario<number>;
  cuotasSelected: CampoFormulario<number>;
  formaPagoSelected: CampoFormulario<string>;
  vigenciaDesdeSelected: CampoFormulario<Date>;
  vigenciaHastaSelected: CampoFormulario<FechaFormulario>;
  premioSelected: CampoFormulario<string | number | null>;
  polizaFileNameSelected: CampoFormulario<string | null>;
  cuponeraFileNameSelected: CampoFormulario<string | null>;
  cedulaMercosurFileNameSelected: CampoFormulario<string | null>;
  tarjetaFileNameSelected: CampoFormulario<string | null>;
  isProcessingPoliza: boolean;
  isProcessingCuponera: boolean;
  isProcessingCedulaMercosur: boolean;
  isProcessingTarjeta: boolean;
  creatingPoliza: boolean;
  polizaCreada: boolean;
  loadingError: boolean;
  isLoading: boolean;
  isLoadingAsegurado: boolean;
  isLoadingUsuarios: boolean;
  failed: boolean;
  currentPoliza: null;
  page: number;
  take: number;
  searchCriteria: string;
}

const setSubmitting = createAction(
  "SET_SUBMITTING_ALTA_MULTI",
  (submitting: boolean) => ({
    submitting,
  })
);

const setNumeroPoliza = createAction(
  "SET_NUMERO_POLIZA_MULTI",
  (numeroPolizaSelected: CampoFormulario<string | null>) => ({
    numeroPolizaSelected,
  })
);

const setAseguradoNombre = createAction(
  "SET_NOMBRE_MULTI",
  (aseguradoNombreSelected: CampoFormulario<string | null>) => ({
    aseguradoNombreSelected,
  })
);

const setAseguradoApellido = createAction(
  "SET_APELLIDO_MULTI",
  (aseguradoApellidoSelected: CampoFormulario<string | null>) => ({
    aseguradoApellidoSelected,
  })
);

const setAseguradoNumeroDocumento = createAction(
  "SET_DOCUMENTO_MULTI",
  (aseguradoNumeroDocumentoSelected: CampoFormulario<string | null>) => ({
    aseguradoNumeroDocumentoSelected,
  })
);

const setDominio = createAction(
  "SET_DOMINIO_MULTI",
  (dominioSelected: CampoFormulario<string | null>) => ({
    dominioSelected,
  })
);

const setRama = createAction(
  "SET_RAMA_MULTI",
  (ramaSelected: CampoFormulario<string>) => ({
    ramaSelected,
  })
);

const setCompania = createAction(
  "SET_COMPANIA_MULTI",
  (companiaSelected: CampoFormulario<string>) => ({
    companiaSelected,
  })
);

const setUsuario = createAction(
  "SET_USUARIO_MULTI",
  (usuarioSelected: UsuarioSeleccionado) => ({
    usuarioSelected,
  })
);

const setCobertura = createAction(
  "SET_COBERTURA_MULTI",
  (coberturaSelected: CampoFormulario<string | null>) => ({
    coberturaSelected,
  })
);

const clearUsuario = createAction("CLEAR_USUARIO_MULTI", () => ({}));

const setVigencia = createAction(
  "SET_VIGENCIA_MULTI",
  (vigenciaSelected: CampoFormulario<number>) => ({
    vigenciaSelected,
  })
);

const setCantidadCuotas = createAction(
  "SET_CANTIDAD_CUOTAS_MULTI",
  (cuotasSelected: CampoFormulario<number>) => ({
    cuotasSelected,
  })
);

const setFormaPago = createAction(
  "SET_MEDIO_PAGO_MULTI",
  (formaPagoSelected: CampoFormulario<string>) => ({
    formaPagoSelected,
  })
);

const setVigenciaDesde = createAction(
  "SET_VIGENCIA_DESDE_MULTI",
  (vigenciaDesdeSelected: CampoFormulario<Date>) => ({
    vigenciaDesdeSelected,
  })
);
const setVigenciaHasta = createAction(
  "SET_VIGENCIA_HASTA_MULTI",
  (vigenciaHastaSelected: CampoFormulario<FechaFormulario>) => ({
    vigenciaHastaSelected,
  })
);

const setPremio = createAction(
  "SET_PREMIO_MULTI",
  (premioSelected: CampoFormulario<string | number | null>) => ({
    premioSelected,
  })
);

const setPolizaFileName = createAction(
  "SET_POLIZA_FILENAME_MULTI",
  (polizaFileNameSelected: CampoFormulario<string | null>) => ({
    polizaFileNameSelected,
  })
);

const setCuponeraFileName = createAction(
  "SET_CUPONERA_FILENAME_MULTI",
  (cuponeraFileNameSelected: CampoFormulario<string | null>) => ({
    cuponeraFileNameSelected,
  })
);

const setCedulaMercosurFileName = createAction(
  "SET_CEDULAMERCOSUR_FILENAME_MULTI",
  (cedulaMercosurFileNameSelected: CampoFormulario<string | null>) => ({
    cedulaMercosurFileNameSelected,
  })
);

const setTarjetaFileName = createAction(
  "SET_TARJETA_FILENAME_MULTI",
  (tarjetaFileNameSelected: CampoFormulario<string | null>) => ({
    tarjetaFileNameSelected,
  })
);

const setIsProcessingPoliza = createAction(
  "SET_PROCESSING_POLIZA_MULTI",
  (isProcessingPoliza: boolean) => ({
    isProcessingPoliza,
  })
);

const setIsProcessingCuponera = createAction(
  "SET_PROCESSING_CUPONERA_MULTI",
  (isProcessingCuponera: boolean) => ({
    isProcessingCuponera,
  })
);

const setIsProcessingCedulaMercosur = createAction(
  "SET_PROCESSING_CEDULAMERCOSUR_MULTI",
  (isProcessingCedulaMercosur: boolean) => ({
    isProcessingCedulaMercosur,
  })
);

const setIsProcessingTarjeta = createAction(
  "SET_PROCESSING_TARJETA_MULTI",
  (isProcessingTarjeta: boolean) => ({
    isProcessingTarjeta,
  })
);

const getAseguradoInited = createAction("OBTENER_ASEGURADO_INITED");

const getAsegurado = (documento: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getAseguradoInited());

      const response = await services.api
        .polizasMultirama()
        .getAsegurado(documento);

      dispatch(getAseguradoCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getAseguradoFailed(error));
    }
  };
};

const getAseguradoCompleted = createAction(
  "OBTENER_ASEGURADO_COMPLETED",
  (asegurado: GetAseguradoResponse) => ({
    asegurado,
  })
);

const getAseguradoFailed = createAction(
  "OBTENER_ASEGURADO_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getRamasInited = createAction("OBTENER_RAMAS_INITED");

const getRamas = () => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getRamasInited());

      const response = await services.api.polizasMultirama().getRamas();

      dispatch(getRamasCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getRamasFailed(error));
    }
  };
};

const getRamasCompleted = createAction(
  "OBTENER_RAMAS_COMPLETED",
  (response: GetRamasResponse) => ({ ramas: response })
);

const getRamasFailed = createAction(
  "OBTENER_RAMAS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getUsuariosInited = createAction("OBTENER_USUARIOS_MULTI_INITED");

const getUsuarios = (skip: number, take: number, searchCriteria: string) => {
  return async (
    dispatch: Dispatch,
    getState: GetState,
    services: ServicesUsuarios
  ) => {
    try {
      dispatch(getUsuariosInited());

      const data = {
        skip: skip,
        take: take,
        searchCriteria: searchCriteria,
      };

      const response = await services.api.usuarios().getUsuarios(data);

      dispatch(getUsuariosCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getUsuariosFailed(error));
    }
  };
};

const getUsuariosCompleted = createAction(
  "OBTENER_USUARIOS_MULTI_COMPLETED",
  (response: GetUsuariosResponse | null | undefined) => {
    let usuarios: Usuario[] = [];

    if (response != null) {
      usuarios = response.usuarios;
    }

    return { usuarios };
  }
);

const getUsuariosFailed = createAction(
  "OBTENER_USUARIOS_MULTI_FAILED",
  (error: unknown) => ({
    error,
  })
);

const postPolizaMultiramaInited = createAction(
  "PROCESAR_SOLICITUD_MULTI_INITED"
);

const postPolizaMultiramaCompleted = createAction(
  "PROCESAR_SOLICITUD_MULTI_COMPLETED",
  (response: PostPolizaMultiRamaResponse) => {}
);

const postPolizaMultiramaFailed = createAction(
  "PROCESAR_SOLICITUD_MULTI_FAILED",
  (error: unknown) => ({
    error,
  })
);

const postPolizaMultirama = (data: PostPolizaMultiRamaRequest) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(postPolizaMultiramaInited());

      const response = await services.api
        .polizasMultirama()
        .postPolizaMultirama(data);

      dispatch(postPolizaMultiramaCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(postPolizaMultiramaFailed(error));

      dispatch(
        queueMessage(
          (error as AltaPolizaError).responseStatus.errors[0].message
        )
      );
    }
  };
};

const clearData = createAction("CLEAR_DATA_MULTI", () => ({}));

const altaPolizaMultiramaReducerInitialState: AltaPolizaMultiramaState = {
  submitting: false,
  numeroPolizaSelected: { value: "", valid: false, focus: false },
  aseguradoNombreSelected: { value: "", valid: false, focus: false },
  aseguradoApellidoSelected: { value: "", valid: false, focus: false },
  aseguradoIdSelected: null,
  aseguradoNumeroDocumentoSelected: { value: "", valid: false, focus: false },
  dominioSelected: { value: "", valid: false, focus: false },
  ramaSelected: {
    value: "motovehiculo",
    valid: false,
    focus: false,
    tieneDominio: true,
  },
  asegurado: null,
  ramasFilter: null,
  ramasOptions: {},
  companiaSelected: { value: "Sancor", valid: false, focus: false },
  coberturaSelected: { value: "", valid: false, focus: false },
  usuarioSelected: {
    label: null,
    value: "0a10c226-3c6a-4c18-99b5-271ff17c4a40",
    valid: false,
    focus: false,
  },
  usuariosFilter: [],
  usuariosOptions: {},
  vigenciaSelected: { value: 6, valid: true, focus: false },
  cuotasSelected: { value: 6, valid: false, focus: false },
  formaPagoSelected: {
    value: "DebitoConTarjetaCredito",
    valid: true,
    focus: false,
  },
  vigenciaDesdeSelected: { value: new Date(), valid: true, focus: false },
  vigenciaHastaSelected: { value: new Date(), valid: false, focus: false },
  premioSelected: { value: 0, valid: false, focus: false },
  polizaFileNameSelected: { value: null, valid: false, focus: false },
  cuponeraFileNameSelected: { value: null, valid: false, focus: false },
  cedulaMercosurFileNameSelected: { value: null, valid: false, focus: false },
  tarjetaFileNameSelected: { value: null, valid: false, focus: false },
  isProcessingPoliza: false,
  isProcessingCuponera: false,
  isProcessingCedulaMercosur: false,
  isProcessingTarjeta: false,
  creatingPoliza: false,
  polizaCreada: false,
  loadingError: false,
  isLoading: false,
  isLoadingAsegurado: false,
  isLoadingUsuarios: false,
  failed: false,
  currentPoliza: null,
  page: 0,
  take: 100,
  searchCriteria: "",
};

const altaPolizaMultiramaReducer = handleActions<AltaPolizaMultiramaState, any>(
  {
    [setSubmitting.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ submitting: boolean }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        submitting: action.payload.submitting,
      };
    },
    [setNumeroPoliza.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ numeroPolizaSelected: CampoFormulario<string | null> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        numeroPolizaSelected: action.payload.numeroPolizaSelected,
      };
    },
    [setAseguradoNombre.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        aseguradoNombreSelected: CampoFormulario<string | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        aseguradoNombreSelected: action.payload.aseguradoNombreSelected,
      };
    },
    [setAseguradoApellido.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        aseguradoApellidoSelected: CampoFormulario<string | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        aseguradoApellidoSelected: action.payload.aseguradoApellidoSelected,
      };
    },
    [setAseguradoNumeroDocumento.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        aseguradoNumeroDocumentoSelected: CampoFormulario<string | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        aseguradoNumeroDocumentoSelected:
          action.payload.aseguradoNumeroDocumentoSelected,
      };
    },
    [setDominio.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ dominioSelected: CampoFormulario<string | null> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        dominioSelected: action.payload.dominioSelected,
      };
    },
    [setRama.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ ramaSelected: CampoFormulario<string> }>
    ): AltaPolizaMultiramaState => {
      /* aca recibimos las opciones de ramaFilter y fusionamos su valor tieneDominio con la ramaSelected que viene del input, nos sirve para armar el condicional que muestra o no el input Dominio */
      const ramaDeFilter = state.ramasFilter!.find(
        (rama) => rama.value === action.payload.ramaSelected.value
      );

      const ramaSelected: RamaSeleccionada = {
        ...action.payload.ramaSelected,
        tieneDominio: ramaDeFilter!.tieneDominio,
      };

      return {
        ...state,
        ramaSelected: ramaSelected,
      };
    },
    [setCompania.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ companiaSelected: CampoFormulario<string> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        companiaSelected: action.payload.companiaSelected,
      };
    },
    [setUsuario.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ usuarioSelected: UsuarioSeleccionado }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        usuarioSelected: action.payload.usuarioSelected,
      };
    },
    [setCobertura.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ coberturaSelected: CampoFormulario<string | null> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        coberturaSelected: action.payload.coberturaSelected,
      };
    },
    [clearUsuario.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        usuarioSelected: {
          label: null,
          value: "",
          valid: false,
          focus: false,
        },
      };
    },
    [setCantidadCuotas.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ cuotasSelected: CampoFormulario<number> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        cuotasSelected: action.payload.cuotasSelected,
      };
    },
    [setFormaPago.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ formaPagoSelected: CampoFormulario<string> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        formaPagoSelected: action.payload.formaPagoSelected,
      };
    },
    [setVigencia.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ vigenciaSelected: CampoFormulario<number> }>
    ): AltaPolizaMultiramaState => {
      const vigencia = action.payload.vigenciaSelected;
      const vigenciaDesde = state.vigenciaDesdeSelected;
      return {
        ...state,
        vigenciaSelected: vigencia,
        vigenciaHastaSelected: {
          value: moment(vigenciaDesde.value).add(vigencia.value, "months"),
          valid: true,
          focus: false,
        },
      };
    },
    [setVigenciaDesde.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ vigenciaDesdeSelected: CampoFormulario<Date> }>
    ): AltaPolizaMultiramaState => {
      const vigenciaDesde = action.payload.vigenciaDesdeSelected;
      const vigenciaHasta = new Date(vigenciaDesde.value);
      const vigencia = state.vigenciaSelected;

      return {
        ...state,
        vigenciaDesdeSelected: vigenciaDesde,
        vigenciaHastaSelected: {
          value: new Date(
            vigenciaHasta.setMonth(vigenciaHasta.getMonth() + vigencia.value)
          ),
          valid: true,
          focus: false,
        },
      };
    },
    [setVigenciaHasta.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        vigenciaHastaSelected: CampoFormulario<FechaFormulario>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        vigenciaHastaSelected: action.payload.vigenciaHastaSelected,
      };
    },
    [setPremio.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        premioSelected: CampoFormulario<string | number | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        premioSelected: action.payload.premioSelected,
      };
    },
    [setIsProcessingPoliza.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ isProcessingPoliza: boolean }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isProcessingPoliza: action.payload.isProcessingPoliza,
      };
    },
    [setIsProcessingCuponera.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ isProcessingCuponera: boolean }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isProcessingCuponera: action.payload.isProcessingCuponera,
      };
    },
    [setIsProcessingCedulaMercosur.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ isProcessingCedulaMercosur: boolean }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isProcessingCedulaMercosur: action.payload.isProcessingCedulaMercosur,
      };
    },
    [setIsProcessingTarjeta.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ isProcessingTarjeta: boolean }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isProcessingTarjeta: action.payload.isProcessingTarjeta,
      };
    },
    [setPolizaFileName.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ polizaFileNameSelected: CampoFormulario<string | null> }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        polizaFileNameSelected: action.payload.polizaFileNameSelected,
      };
    },
    [setCuponeraFileName.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        cuponeraFileNameSelected: CampoFormulario<string | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        cuponeraFileNameSelected: action.payload.cuponeraFileNameSelected,
      };
    },
    [setCedulaMercosurFileName.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        cedulaMercosurFileNameSelected: CampoFormulario<string | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        cedulaMercosurFileNameSelected:
          action.payload.cedulaMercosurFileNameSelected,
      };
    },
    [setTarjetaFileName.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{
        tarjetaFileNameSelected: CampoFormulario<string | null>;
      }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        tarjetaFileNameSelected: action.payload.tarjetaFileNameSelected,
      };
    },
    [getRamasInited.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isLoading: true,
        isLoadingAsegurado: true,
      };
    },
    [getRamasCompleted.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ ramas: GetRamasResponse }>
    ): AltaPolizaMultiramaState => {
      let ramasFilter: RamaOption[] = action.payload.ramas.ramas.map(
        (rama) => ({
          label: rama.nombre,
          value: rama.id,
          tieneDominio: rama.tieneDominio,
        })
      );

      let ramasOptions: Record<string, string> = {};

      action.payload.ramas.ramas.forEach((rama) => {
        ramasOptions[rama.id] = rama.nombre;
      });

      return {
        ...state,
        ramasFilter: ramasFilter,
        ramasOptions: ramasOptions,
        isLoading: false,
        isLoadingAsegurado: false,
      };
    },
    [getRamasFailed.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        loadingError: true,
        isLoading: false,
        isLoadingAsegurado: false,
      };
    },
    [getUsuariosInited.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isLoadingUsuarios: true,
      };
    },
    [getUsuariosCompleted.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ usuarios: Usuario[] }>
    ): AltaPolizaMultiramaState => {
      let usuariosFilter: UsuarioOption[] = action.payload.usuarios.map(
        (usuario) => ({
          label: usuario.nombre,
          value: usuario.id,
        })
      );

      let usuariosOptions: Record<string, string> = {};

      action.payload.usuarios.forEach((usuario) => {
        usuariosOptions[usuario.id] = usuario.nombre;
      });

      return {
        ...state,
        usuariosFilter: usuariosFilter,
        usuariosOptions: usuariosOptions,
        usuarios: action.payload.usuarios,
        isLoadingUsuarios: false,
      };
    },
    [getUsuariosFailed.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isLoadingUsuarios: false,
      };
    },
    [getAseguradoInited.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        isLoadingAsegurado: true,
      };
    },
    [getAseguradoCompleted.toString()]: (
      state: AltaPolizaMultiramaState,
      action: Action<{ asegurado: GetAseguradoResponse }>
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        asegurado: action.payload.asegurado,
        aseguradoNombreSelected: {
          value: action.payload.asegurado.nombre,
          valid: true,
          focus: false,
        },
        aseguradoApellidoSelected: {
          value: action.payload.asegurado.apellido,
          valid: true,
          focus: false,
        },
        aseguradoIdSelected: action.payload.asegurado.id,
        isLoadingAsegurado: false,
      };
    },
    [getAseguradoFailed.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        asegurado: null,
        aseguradoNombreSelected: {
          value: "",
          valid: false,
          focus: false,
        },
        aseguradoApellidoSelected: {
          value: "",
          valid: false,
          focus: false,
        },
        aseguradoIdSelected: null,
        isLoadingAsegurado: false,
      };
    },
    [postPolizaMultiramaInited.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        creatingPoliza: true,
        isLoading: true,
      };
    },
    [postPolizaMultiramaCompleted.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        polizaCreada: true,
        creatingPoliza: false,
        isLoading: false,
      };
    },
    [postPolizaMultiramaFailed.toString()]: (
      state: AltaPolizaMultiramaState
    ): AltaPolizaMultiramaState => {
      return {
        ...state,
        creatingPoliza: false,
        isLoading: false,
      };
    },
    [clearData.toString()]: (): AltaPolizaMultiramaState => {
      return {
        ...altaPolizaMultiramaReducerInitialState,
      };
    },
  },

  altaPolizaMultiramaReducerInitialState
);

export {
  altaPolizaMultiramaReducer,
  setNumeroPoliza,
  setRama,
  setCompania,
  setUsuario,
  setAseguradoNombre,
  setAseguradoApellido,
  setAseguradoNumeroDocumento,
  setDominio,
  setVigencia,
  setCobertura,
  setCantidadCuotas,
  setVigenciaDesde,
  setVigenciaHasta,
  setPremio,
  setSubmitting,
  setFormaPago,
  getRamas,
  getUsuarios,
  setPolizaFileName,
  setCuponeraFileName,
  setCedulaMercosurFileName,
  setTarjetaFileName,
  setIsProcessingPoliza,
  setIsProcessingCuponera,
  setIsProcessingCedulaMercosur,
  setIsProcessingTarjeta,
  postPolizaMultirama,
  clearData,
  clearUsuario,
  getAsegurado,
};
